import clsx from "clsx";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { closeAssistedSearch, closeCallout, openCallout } from "web/redux/ducks/app-header-bar";
import styles from "./callout.module.css";

interface CalloutContextProps {
    opened: boolean;
    disabled: boolean;
    styleTop: string;
    onClickCTA: (any) => void;
}

const CalloutContext = React.createContext<CalloutContextProps>({
    opened: false,
    disabled: false,
    styleTop: "0px",
    onClickCTA: () => {},
});

interface CalloutProps extends ReactComponentProps {
    alignment?: "centre" | "right";
    disabled?: boolean;
    hideDropdown?: boolean;
}

function CalloutCTA({ children }: ReactComponentProps): React.ReactElement {
    const { opened, onClickCTA, disabled } = React.useContext(CalloutContext);

    return (
        <div onClick={onClickCTA}>
            {children}
            {!disabled && (
                <span
                    className={clsx(styles.triangle, {
                        [styles.triangleActive]: opened,
                    })}
                />
            )}
        </div>
    );
}

function CalloutContent({ children }: ReactComponentProps): React.ReactElement | null {
    const { opened, styleTop, disabled } = React.useContext(CalloutContext);

    if (disabled || !opened) {
        return null;
    }

    return (
        <div
            className={clsx(styles.content, {
                [styles.contentActive]: opened,
            })}
            style={{ top: styleTop }}
        >
            {children}
        </div>
    );
}

function Callout({
    disabled = false,
    children,
    className,
    alignment = "right",
}: CalloutProps): React.ReactElement {
    const [styleTop, setStyleTop] = React.useState<string>("0px");
    const [opened, setOpened] = React.useState<boolean>(false);
    const calloutRef = React.useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    function _closeCallout(): void {
        dispatch(closeCallout());
        setOpened(false);
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (!calloutRef?.current?.contains?.(event.target as Node) && opened) {
                _closeCallout();
            }
        }

        document.addEventListener("mouseup", handleClickOutside);

        return (): void => {
            document.removeEventListener("mouseup", handleClickOutside);
        };
    }, [calloutRef, opened, dispatch]);

    function _positionCallout(): void {
        if (!calloutRef?.current) {
            return;
        }

        const TOP_OFFSET = 10;
        const calloutOffset = calloutRef.current.getBoundingClientRect().height;

        setStyleTop(`${calloutOffset + TOP_OFFSET}px`);
    }

    function onClickCTA(): void {
        if (!opened && !disabled) {
            _positionCallout();
            dispatch(closeAssistedSearch());
            dispatch(openCallout());
            setOpened(true);
        }

        if (opened && !disabled) {
            _closeCallout();
        }
    }

    return (
        <CalloutContext.Provider value={{ opened, onClickCTA, styleTop, disabled }}>
            <div
                className={clsx(
                    styles.callout,
                    {
                        [styles.rightAligned]: alignment === "right",
                        [styles.centreAligned]: alignment === "centre",
                    },
                    className
                )}
                ref={calloutRef}
            >
                {children}
            </div>
        </CalloutContext.Provider>
    );
}

Callout.CTA = CalloutCTA;
Callout.Content = CalloutContent;

export default Callout;
